/**
 * TranslationsSection — låter värden skriva in engelska översättningar
 * för sina texter (short_description, description, food_description).
 *
 * Sparar direkt till content_translations med status='approved' — värden
 * skriver sin egen text, inget review-steg behövs.
 *
 * Se docs/SoT/localization.md §5.
 */

import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { LocaleFlag } from '@/components/common/LocaleFlag';
import { supabase } from '@/integrations/supabase/client';
import { usePendingTranslations } from '@/hooks/usePendingTranslations';
import { RichTextRenderer } from '@/components/common/RichTextRenderer';
import type { TranslatableEntityType } from '@/i18n';

interface FieldConfig {
  key: string;         // content_translations field_name
  label: string;       // Swedish UI label
  svSource: string;    // current Swedish value
  multiline?: boolean; // textarea vs rich text display
}

interface TranslationsSectionProps {
  producerId: string;
  shortDescription: string;
  longDescription: string;
  foodDescription?: string | null;
}

/** Local state shape: field_name → EN draft value */
type DraftMap = Record<string, string>;

function stripHtml(html: string): string {
  return html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
}

export function TranslationsSection({
  producerId,
  shortDescription,
  longDescription,
  foodDescription,
}: TranslationsSectionProps) {
  const qc = useQueryClient();
  const ENTITY_TYPE: TranslatableEntityType = 'producer';
  const LOCALE = 'en';

  // Fetch existing approved/draft EN translations
  const { data: existing = [], isLoading } = usePendingTranslations(ENTITY_TYPE, producerId);

  const fields: FieldConfig[] = [
    shortDescription?.trim()
      ? { key: 'short_description', label: 'Kort beskrivning', svSource: shortDescription }
      : null,
    longDescription?.trim()
      ? { key: 'description', label: 'Beskrivning', svSource: longDescription, multiline: true }
      : null,
    foodDescription?.trim()
      ? { key: 'food_description', label: 'Mat & fika', svSource: foodDescription, multiline: true }
      : null,
  ].filter(Boolean) as FieldConfig[];

  // Initialise drafts from existing DB rows once loaded
  const [drafts, setDrafts] = useState<DraftMap>({});
  const [savedSnapshot, setSavedSnapshot] = useState<DraftMap>({});

  useEffect(() => {
    if (isLoading) return;
    const initial: DraftMap = {};
    for (const f of fields) {
      const row = existing.find((r) => r.field_name === f.key && r.locale === LOCALE);
      initial[f.key] = row?.value ?? '';
    }
    setDrafts(initial);
    setSavedSnapshot(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, existing.length]);

  const isDirty = fields.some((f) => drafts[f.key] !== savedSnapshot[f.key]);

  const upsertMutation = useMutation({
    mutationFn: async (rows: { field_name: string; value: string }[]) => {
      const payload = rows.map((r) => ({
        entity_type: ENTITY_TYPE,
        entity_id: producerId,
        field_name: r.field_name,
        locale: LOCALE,
        source_locale: 'sv',
        value: r.value,
        status: 'approved' as const,
        generated_by_ai: false,
      }));
      const { error } = await supabase
        .from('content_translations')
        .upsert(payload, {
          onConflict: 'entity_type,entity_id,field_name,locale',
          ignoreDuplicates: false,
        });
      if (error) throw error;
    },
    onSuccess: () => {
      setSavedSnapshot({ ...drafts });
      // Invalidate public render cache
      qc.invalidateQueries({ queryKey: ['content_translations', ENTITY_TYPE, producerId] });
      qc.invalidateQueries({ queryKey: ['content_translations', 'pending', ENTITY_TYPE, producerId] });
    },
  });

  const handleSave = () => {
    const rows = fields
      .filter((f) => drafts[f.key] !== undefined)
      .map((f) => ({ field_name: f.key, value: drafts[f.key] ?? '' }));
    upsertMutation.mutate(rows);
  };

  if (fields.length === 0) {
    return (
      <div className="rounded-lg border border-border bg-muted/30 p-6 text-center">
        <p className="text-sm text-muted-foreground">
          Lägg till beskrivning under <strong>Grundinfo</strong> för att kunna lägga till engelska översättningar.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-8 max-w-3xl">
      <div>
        <p className="text-sm text-muted-foreground leading-relaxed">
          Texterna nedan visas för besökare som väljer engelska på er sida. Skriv en naturlig
          översättning — du behöver inte översätta ord för ord.
        </p>
      </div>

      {fields.map((field) => (
        <div key={field.key} className="rounded-xl border border-border bg-card p-5 space-y-4">
          <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {field.label}
          </span>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Swedish source — read-only */}
            <div className="space-y-1.5">
              <div className="flex items-center gap-1.5">
                <LocaleFlag locale="sv" />
                <Label className="text-xs text-muted-foreground">Svenska (original)</Label>
              </div>
              {field.multiline ? (
                <div className="text-sm text-foreground leading-relaxed farmly-richtext max-h-48 overflow-y-auto pr-1">
                  <RichTextRenderer html={field.svSource} />
                </div>
              ) : (
                <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">
                  {field.svSource}
                </p>
              )}
            </div>

            {/* English — editable */}
            <div className="space-y-1.5">
              <div className="flex items-center gap-1.5">
                <LocaleFlag locale="en" />
                <Label htmlFor={`en-${field.key}`} className="text-xs text-muted-foreground">
                  English (translation)
                </Label>
              </div>
              <Textarea
                id={`en-${field.key}`}
                value={drafts[field.key] ?? ''}
                onChange={(e) =>
                  setDrafts((prev) => ({ ...prev, [field.key]: e.target.value }))
                }
                placeholder={
                  field.multiline
                    ? `English translation of "${stripHtml(field.svSource).slice(0, 60)}…"`
                    : `English translation…`
                }
                rows={field.multiline ? 6 : 3}
                className="text-sm resize-y"
                data-testid={`translation-en-${field.key}`}
              />
              {field.multiline && (
                <p className="text-[11px] text-muted-foreground">
                  Plain text — formatering från originaltext behålls inte.
                </p>
              )}
            </div>
          </div>
        </div>
      ))}

      {/* Save bar */}
      <div className="flex items-center justify-between pt-2 border-t border-border">
        {isDirty && (
          <span className="text-xs text-amber-600 font-medium">Osparade ändringar</span>
        )}
        <div className="ml-auto">
          <button
            type="button"
            onClick={handleSave}
            disabled={upsertMutation.isPending || !isDirty}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-primary/90 transition-colors"
            data-testid="translations-save"
          >
            {upsertMutation.isPending ? 'Sparar…' : 'Spara översättningar'}
          </button>
        </div>
      </div>

      {upsertMutation.isError && (
        <p className="text-sm text-destructive">
          Kunde inte spara. Försök igen.
        </p>
      )}
    </div>
  );
}
